import { useCallback, useRef, useSyncExternalStore } from 'react'
import { QueryClient, useQueryClient } from 'react-query'
import { Channel } from 'stream-chat'
import { getCaseDetailsQueryPrefix } from '@/pages/Case/CaseV2/hooks/useCaseDetails'
import { Case } from '@/pages/Case/CaseV2/types'
import { isBuyerContactWithheld } from '@/pages/Case/CaseV2/withholdClientContact'

/** A case channel's case id, or '' for any other channel. */
export const getChannelCaseId = (channel: Channel): string =>
  channel.data?.case_id ? String(channel.data.case_id) : ''

const CASE_DETAILS_KEY_HEAD = getCaseDetailsQueryPrefix()[0]

/**
 * The masked title of a case withheld from this viewer, read from the cache.
 *
 * A case can be cached under several key variants (one per set of fetch
 * flags), and they need not agree: a variant fetched before the match, or
 * before the BFF knew to mask, says nothing is withheld. Fails closed across
 * them -- if ANY variant says the case is withheld from this viewer, its
 * masked title wins, the most recently updated one when several do.
 */
const findWithheldCaseTitle = (
  queryClient: QueryClient,
  caseId: string
): string | null => {
  if (!caseId) return null

  const withheld = queryClient
    .getQueryCache()
    .findAll(getCaseDetailsQueryPrefix(caseId))
    .map(query => ({
      data: query.state.data as Case | undefined,
      updatedAt: query.state.dataUpdatedAt,
    }))
    .filter(
      ({ data }) =>
        data?.id === caseId && isBuyerContactWithheld(data) && !!data.name
    )
    .sort((a, b) => b.updatedAt - a.updatedAt)

  return withheld[0]?.data?.name || null
}

/**
 * A counter that moves whenever any case's cached details change (a fetch
 * lands, data is set, a query is dropped), so the lookup below keeps one
 * identity between those changes and memoised callers do not recompute on
 * every render.
 */
const useCaseDetailsCacheVersion = (queryClient: QueryClient): number => {
  const version = useRef(0)

  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe(event => {
        if (!event) return
        const key = event.query.queryKey
        if (!Array.isArray(key) || key[0] !== CASE_DETAILS_KEY_HEAD) return
        const dataChanged =
          event.type === 'queryRemoved' ||
          (event.type === 'queryUpdated' &&
            (event.action.type === 'success' ||
              event.action.type === 'setState'))
        if (!dataChanged) return
        version.current += 1
        onChange()
      }),
    [queryClient]
  )

  return useSyncExternalStore(subscribe, () => version.current)
}

/**
 * Withhold Client Contact: looks up the viewer's own case title for a case
 * whose client contact info is withheld from them.
 *
 * A case channel's stored `caseTitle` is whatever its creator's page showed,
 * and a channel the seller opened can carry the client's full name. The
 * buyer's own title comes from the BFF already masked ("(Maria G.) …"), so
 * it is preferred while withheld.
 *
 * Reads only what is already cached -- it never fetches -- under every
 * variant of the case-details key, so it keeps working however a caller
 * threads the fetch flags. Returns null for a case that is not cached or not
 * withheld from this viewer, leaving the stored title.
 *
 * The returned function is stable until a case's cached details change.
 *
 * Limits: a case the viewer has not opened this session is not cached, so
 * its stored title shows; and Stream's server-side channel search still
 * matches on the stored title.
 */
export const useWithheldCaseTitleLookup = (): ((
  caseId: string
) => string | null) => {
  const queryClient = useQueryClient()
  const cacheVersion = useCaseDetailsCacheVersion(queryClient)

  return useCallback(
    (caseId: string) => findWithheldCaseTitle(queryClient, caseId),
    // cacheVersion is the point: a new lookup once the cache has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, cacheVersion]
  )
}

/** The masked title for one case channel; see useWithheldCaseTitleLookup. */
export const useWithheldCaseTitle = (channel: Channel): string | null =>
  useWithheldCaseTitleLookup()(getChannelCaseId(channel))
